import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CalendarRange } from 'lucide-react';
import { PromotionPageLayout } from '@/components/layout/PromotionPageLayout';
import { PromotionModalHeader } from '@/components/layout/PromotionModalHeader';
import { PromotionModalFooter } from '@/components/layout/PromotionModalFooter';
import { InfoBar } from '@/components/common/InfoBar';
import { DaysPicker } from './components/DaysPicker';
import { TimeSlotsPicker } from './components/TimeSlotsPicker';
import { ROUTES } from '@/constants/routes';

export default function PromotionAvailability() {
  const navigate = useNavigate();
  const location = useLocation();
  const incoming = location.state ?? {};

  useEffect(() => {
    if (!incoming.role) {
      navigate(ROUTES.promotionEnrollment, { replace: true });
    }
  }, [incoming.role, navigate]);

  const [selectedDays, setSelectedDays] = useState(incoming.days ?? []);
  const [timeSlots, setTimeSlots] = useState(incoming.timeSlots ?? {});

  function toggleDay(day) {
    setSelectedDays((prev) => {
      if (prev.includes(day)) {
        setTimeSlots((slots) => {
          const next = { ...slots };
          delete next[day];
          return next;
        });
        return prev.filter((d) => d !== day);
      }
      return [...prev, day];
    });
  }

  function toggleSlot(day, slot) {
    setTimeSlots((prev) => {
      const current = prev[day] ?? [];
      const next = current.includes(slot)
        ? current.filter((s) => s !== slot)
        : [...current, slot];
      return { ...prev, [day]: next };
    });
  }

  const allDaysHaveSlots =
    selectedDays.length > 0 &&
    selectedDays.every((day) => (timeSlots[day] ?? []).length > 0);

  function handleSubmit(e) {
    e.preventDefault();
    if (!allDaysHaveSlots) return;
    navigate(ROUTES.promotionContact, {
      state: {
        ...incoming,
        days: selectedDays,
        timeSlots,
      },
    });
  }

  return (
    <PromotionPageLayout step={3} totalSteps={4} modal modalSize="lg">
      <PromotionModalHeader
        icon={CalendarRange}
        title="When are you available?"
        subtitle="Tap the days and times that work. We will make every effort to work with your availability."
        backTo={ROUTES.promotionProfile}
        backState={incoming}
      />
      <InfoBar variant="solid" size="sm" className="mt-5">
        Class times may be subject to adjustments.
      </InfoBar>
      <form onSubmit={handleSubmit}>
        <div className="space-y-7 px-5 py-6 sm:px-8 sm:py-7">
          <DaysPicker selectedDays={selectedDays} onToggle={toggleDay} />
          <TimeSlotsPicker
            selectedDays={selectedDays}
            timeSlots={timeSlots}
            onToggle={toggleSlot}
          />
        </div>
        <PromotionModalFooter disabled={!allDaysHaveSlots}>
          Continue
        </PromotionModalFooter>
      </form>
    </PromotionPageLayout>
  );
}